/**
 * DANAmark Admin — Navigation editor.
 *
 * Edits `site_settings.data.header_nav` (array of top-level nav items, some
 * with `children`) and `site_settings.data.footer_nav_groups` (array of
 * groups with `links`). Reuses the generic site-settings PUT endpoint so
 * every change persists immediately with no code deploy.
 *
 * Scope: this tab only exposes the fields explicitly listed in the brief:
 * ID, labels (EN/DA/ES), URL, type/parent, order, visibility, emphasis
 * and new-tab behaviour. Visual drag-and-drop is deliberately avoided —
 * reorder is handled by simple ▲ / ▼ buttons.
 */
import React, { useEffect, useState, useMemo } from "react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "da", label: "Dansk" },
  { code: "es", label: "Español" },
];
const emptyI18n = () => ({ en: "", da: "", es: "" });

export default function NavigationAdmin({ authed }) {
  const [settings, setSettings] = useState(null);
  const [header, setHeader] = useState([]);
  const [footer, setFooter] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const r = await authed.get("/site-settings");
      setSettings(r.data || {});
      setHeader(Array.isArray(r.data?.header_nav) ? r.data.header_nav : []);
      setFooter(Array.isArray(r.data?.footer_nav_groups) ? r.data.footer_nav_groups : []);
    } catch (e) {
      setErr(stringifyError(e));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      await authed.put("/admin/site-settings", {
        ...(settings || {}),
        header_nav: header,
        footer_nav_groups: footer,
      });
      setMsg("Saved — navigation updated.");
      setTimeout(() => setMsg(""), 3500);
    } catch (e) {
      setErr(stringifyError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="nav-admin">
      <section className="border border-line rounded-xl p-6 bg-white flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="label-mono text-terracotta text-[10px]">GLOBAL · NAVIGATION</div>
          <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">Header &amp; Footer menus</h2>
          <p className="text-sm text-ink/60 mt-2 max-w-xl">
            Edit labels in English, Danish and Spanish, URLs, display order and visibility.
            Changes publish immediately — no rebuild needed.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {msg && <span className="label-mono text-emerald-700 text-[10px]">{msg}</span>}
          {err && <span className="label-mono text-red-600 text-[10px]">{err}</span>}
          <button onClick={save} disabled={saving} data-testid="nav-admin-save"
                  className="px-5 py-2.5 bg-terracotta text-white text-[11px] uppercase tracking-widest disabled:opacity-50">
            {saving ? "Saving…" : "Save navigation"}
          </button>
        </div>
      </section>

      <HeaderNavEditor header={header} setHeader={setHeader} />
      <FooterNavEditor footer={footer} setFooter={setFooter} />
    </div>
  );
}

// ─── HEADER editor ──────────────────────────────────────────────────────
function HeaderNavEditor({ header, setHeader }) {
  const sorted = useMemo(
    () => header.slice().map((x, i) => ({ ...x, __idx: i })).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [header]
  );

  const update = (idx, patch) => setHeader(prev => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const remove = (idx) => setHeader(prev => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) => setHeader(prev => {
    const items = prev.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const pos = items.findIndex(x => x === prev[idx]);
    const other = pos + (dir === "up" ? -1 : 1);
    if (other < 0 || other >= items.length) return prev;
    const a = items[pos], b = items[other];
    const ao = a.order, bo = b.order;
    return prev.map(x => {
      if (x === a) return { ...x, order: bo };
      if (x === b) return { ...x, order: ao };
      return x;
    });
  });
  const addItem = () => {
    const nextOrder = (header.reduce((m, x) => Math.max(m, x.order ?? 0), 0) + 10);
    setHeader(prev => [...prev, {
      id: `item_${Date.now().toString(36)}`, type: "link", url: "/",
      order: nextOrder, show: true, emphasis: "default",
      label: emptyI18n(),
    }]);
  };
  const updateChild = (parentIdx, childIdx, patch) => setHeader(prev => prev.map((x, i) => {
    if (i !== parentIdx) return x;
    const ch = (x.children || []).map((c, j) => j === childIdx ? { ...c, ...patch } : c);
    return { ...x, children: ch };
  }));
  const removeChild = (parentIdx, childIdx) => setHeader(prev => prev.map((x, i) => {
    if (i !== parentIdx) return x;
    return { ...x, children: (x.children || []).filter((_, j) => j !== childIdx) };
  }));
  const addChild = (parentIdx) => setHeader(prev => prev.map((x, i) => {
    if (i !== parentIdx) return x;
    const next = (x.children || []).slice();
    const nextOrder = next.reduce((m, c) => Math.max(m, c.order ?? 0), 0) + 10;
    next.push({ id: `child_${Date.now().toString(36)}`, url: "/", order: nextOrder, show: true, label: emptyI18n() });
    return { ...x, children: next };
  }));

  return (
    <section className="border border-line rounded-xl p-6 bg-white" data-testid="nav-admin-header-section">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-base">Header navigation</h3>
        <button onClick={addItem} data-testid="nav-admin-header-add"
                className="text-[11px] uppercase tracking-widest border border-ink hover:bg-ink hover:text-white px-3 py-1.5">
          + Add item
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {sorted.map(it => {
          const idx = it.__idx;
          return (
            <div key={idx} className="border border-line rounded p-4 bg-cream/40" data-testid={`nav-admin-header-item-${it.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="text-[11px] text-ink/50">#{it.id}</code>
                <select value={it.type || "link"} onChange={e => update(idx, { type: e.target.value })}
                        data-testid={`nav-admin-header-type-${it.id}`}
                        className="border border-line rounded px-2 py-1 text-xs">
                  <option value="link">Link</option>
                  <option value="dropdown">Dropdown</option>
                </select>
                <input value={it.url || ""} onChange={e => update(idx, { url: e.target.value })}
                       data-testid={`nav-admin-header-url-${it.id}`}
                       placeholder="/url"
                       className="border border-line rounded px-2 py-1 text-xs flex-1 min-w-[160px]" />
                <select value={it.emphasis || "default"} onChange={e => update(idx, { emphasis: e.target.value })}
                        data-testid={`nav-admin-header-emphasis-${it.id}`}
                        className="border border-line rounded px-2 py-1 text-xs">
                  <option value="default">Default</option>
                  <option value="raffle">Raffle pill</option>
                  <option value="primary">Join button</option>
                </select>
                <label className="inline-flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={!!it.new_tab}
                         onChange={e => update(idx, { new_tab: e.target.checked })} /> new tab
                </label>
                <label className="inline-flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={it.show !== false}
                         onChange={e => update(idx, { show: e.target.checked })}
                         data-testid={`nav-admin-header-show-${it.id}`} /> visible
                </label>
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => move(idx, "up")}
                          data-testid={`nav-admin-header-up-${it.id}`}
                          className="px-2 text-xs border border-line rounded hover:bg-line/30">▲</button>
                  <button onClick={() => move(idx, "down")}
                          data-testid={`nav-admin-header-down-${it.id}`}
                          className="px-2 text-xs border border-line rounded hover:bg-line/30">▼</button>
                  <button onClick={() => remove(idx)}
                          data-testid={`nav-admin-header-remove-${it.id}`}
                          className="px-2 text-xs border border-red-200 text-red-700 rounded hover:bg-red-50">×</button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                {LANGS.map(l => (
                  <input key={l.code} value={it.label?.[l.code] || ""}
                         data-testid={`nav-admin-header-label-${it.id}-${l.code}`}
                         onChange={e => update(idx, { label: { ...(it.label || emptyI18n()), [l.code]: e.target.value } })}
                         placeholder={`Label — ${l.label}`}
                         className="border border-line rounded px-2 py-1.5 text-xs" />
                ))}
              </div>

              {it.type === "dropdown" && (
                <div className="mt-4 border-t border-line/70 pt-3">
                  <div className="flex items-center justify-between">
                    <div className="label-mono text-[10px] text-ink/60">CHILD ITEMS</div>
                    <button onClick={() => addChild(idx)}
                            data-testid={`nav-admin-header-add-child-${it.id}`}
                            className="text-[10px] uppercase tracking-widest border border-ink hover:bg-ink hover:text-white px-2 py-1">
                      + Add child
                    </button>
                  </div>
                  <div className="mt-2 space-y-2">
                    {(it.children || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((c) => {
                      const cIdx = (it.children || []).indexOf(c);
                      return (
                        <div key={c.id} className="border border-line rounded p-2 bg-white"
                             data-testid={`nav-admin-header-child-${it.id}-${c.id}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <code className="text-[10px] text-ink/50">#{c.id}</code>
                            <input value={c.url || ""} onChange={e => updateChild(idx, cIdx, { url: e.target.value })}
                                   data-testid={`nav-admin-header-child-url-${it.id}-${c.id}`}
                                   placeholder="/url"
                                   className="border border-line rounded px-2 py-1 text-xs flex-1 min-w-[140px]" />
                            <input type="number" value={c.order ?? 0}
                                   onChange={e => updateChild(idx, cIdx, { order: parseInt(e.target.value, 10) || 0 })}
                                   className="border border-line rounded px-2 py-1 text-xs w-16" />
                            <label className="inline-flex items-center gap-1 text-xs">
                              <input type="checkbox" checked={c.show !== false}
                                     onChange={e => updateChild(idx, cIdx, { show: e.target.checked })} /> visible
                            </label>
                            <button onClick={() => removeChild(idx, cIdx)}
                                    data-testid={`nav-admin-header-child-remove-${it.id}-${c.id}`}
                                    className="ml-auto px-2 text-xs border border-red-200 text-red-700 rounded hover:bg-red-50">×</button>
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            {LANGS.map(l => (
                              <input key={l.code} value={c.label?.[l.code] || ""}
                                     data-testid={`nav-admin-header-child-label-${it.id}-${c.id}-${l.code}`}
                                     onChange={e => updateChild(idx, cIdx, { label: { ...(c.label || emptyI18n()), [l.code]: e.target.value } })}
                                     placeholder={`Label — ${l.label}`}
                                     className="border border-line rounded px-2 py-1 text-xs" />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── FOOTER editor ──────────────────────────────────────────────────────
function FooterNavEditor({ footer, setFooter }) {
  const sorted = useMemo(
    () => footer.slice().map((x, i) => ({ ...x, __idx: i })).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [footer]
  );

  const update = (idx, patch) => setFooter(prev => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const remove = (idx) => setFooter(prev => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) => setFooter(prev => {
    const items = prev.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const pos = items.findIndex(x => x === prev[idx]);
    const other = pos + (dir === "up" ? -1 : 1);
    if (other < 0 || other >= items.length) return prev;
    const a = items[pos], b = items[other];
    const ao = a.order, bo = b.order;
    return prev.map(x => {
      if (x === a) return { ...x, order: bo };
      if (x === b) return { ...x, order: ao };
      return x;
    });
  });
  const addGroup = () => {
    const nextOrder = (footer.reduce((m, x) => Math.max(m, x.order ?? 0), 0) + 10);
    setFooter(prev => [...prev, {
      id: `group_${Date.now().toString(36)}`,
      order: nextOrder, show: true, label: emptyI18n(), links: [],
    }]);
  };
  const updateLink = (gIdx, lIdx, patch) => setFooter(prev => prev.map((x, i) => {
    if (i !== gIdx) return x;
    const links = (x.links || []).map((l, j) => j === lIdx ? { ...l, ...patch } : l);
    return { ...x, links };
  }));
  const removeLink = (gIdx, lIdx) => setFooter(prev => prev.map((x, i) => {
    if (i !== gIdx) return x;
    return { ...x, links: (x.links || []).filter((_, j) => j !== lIdx) };
  }));
  const addLink = (gIdx) => setFooter(prev => prev.map((x, i) => {
    if (i !== gIdx) return x;
    const links = (x.links || []).slice();
    const nextOrder = links.reduce((m, l) => Math.max(m, l.order ?? 0), 0) + 10;
    links.push({ id: `link_${Date.now().toString(36)}`, url: "/", order: nextOrder, show: true, label: emptyI18n() });
    return { ...x, links };
  }));

  return (
    <section className="border border-line rounded-xl p-6 bg-white" data-testid="nav-admin-footer-section">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-base">Footer navigation</h3>
        <button onClick={addGroup} data-testid="nav-admin-footer-add"
                className="text-[11px] uppercase tracking-widest border border-ink hover:bg-ink hover:text-white px-3 py-1.5">
          + Add group
        </button>
      </div>
      <div className="mt-4 space-y-4">
        {sorted.map(g => {
          const idx = g.__idx;
          return (
            <div key={idx} className="border border-line rounded p-4 bg-cream/40"
                 data-testid={`nav-admin-footer-group-${g.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="text-[11px] text-ink/50">#{g.id}</code>
                <label className="inline-flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={g.show !== false}
                         onChange={e => update(idx, { show: e.target.checked })} /> visible
                </label>
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => move(idx, "up")}
                          data-testid={`nav-admin-footer-up-${g.id}`}
                          className="px-2 text-xs border border-line rounded hover:bg-line/30">▲</button>
                  <button onClick={() => move(idx, "down")}
                          data-testid={`nav-admin-footer-down-${g.id}`}
                          className="px-2 text-xs border border-line rounded hover:bg-line/30">▼</button>
                  <button onClick={() => remove(idx)}
                          data-testid={`nav-admin-footer-remove-${g.id}`}
                          className="px-2 text-xs border border-red-200 text-red-700 rounded hover:bg-red-50">×</button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                {LANGS.map(l => (
                  <input key={l.code} value={g.label?.[l.code] || ""}
                         data-testid={`nav-admin-footer-group-label-${g.id}-${l.code}`}
                         onChange={e => update(idx, { label: { ...(g.label || emptyI18n()), [l.code]: e.target.value } })}
                         placeholder={`Group label — ${l.label}`}
                         className="border border-line rounded px-2 py-1.5 text-xs" />
                ))}
              </div>

              <div className="mt-4 border-t border-line/70 pt-3">
                <div className="flex items-center justify-between">
                  <div className="label-mono text-[10px] text-ink/60">LINKS</div>
                  <button onClick={() => addLink(idx)}
                          data-testid={`nav-admin-footer-add-link-${g.id}`}
                          className="text-[10px] uppercase tracking-widest border border-ink hover:bg-ink hover:text-white px-2 py-1">
                    + Add link
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  {(g.links || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((l) => {
                    const lIdx = (g.links || []).indexOf(l);
                    return (
                      <div key={l.id} className="border border-line rounded p-2 bg-white"
                           data-testid={`nav-admin-footer-link-${g.id}-${l.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-[10px] text-ink/50">#{l.id}</code>
                          <input value={l.url || ""} onChange={e => updateLink(idx, lIdx, { url: e.target.value })}
                                 data-testid={`nav-admin-footer-link-url-${g.id}-${l.id}`}
                                 placeholder="/url"
                                 className="border border-line rounded px-2 py-1 text-xs flex-1 min-w-[140px]" />
                          <input type="number" value={l.order ?? 0}
                                 data-testid={`nav-admin-footer-link-order-${g.id}-${l.id}`}
                                 onChange={e => updateLink(idx, lIdx, { order: parseInt(e.target.value, 10) || 0 })}
                                 className="border border-line rounded px-2 py-1 text-xs w-16" />
                          <label className="inline-flex items-center gap-1 text-xs">
                            <input type="checkbox" checked={l.show !== false}
                                   onChange={e => updateLink(idx, lIdx, { show: e.target.checked })} /> visible
                          </label>
                          <button onClick={() => removeLink(idx, lIdx)}
                                  data-testid={`nav-admin-footer-link-remove-${g.id}-${l.id}`}
                                  className="ml-auto px-2 text-xs border border-red-200 text-red-700 rounded hover:bg-red-50">×</button>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          {LANGS.map(ln => (
                            <input key={ln.code} value={l.label?.[ln.code] || ""}
                                   data-testid={`nav-admin-footer-link-label-${g.id}-${l.id}-${ln.code}`}
                                   onChange={e => updateLink(idx, lIdx, { label: { ...(l.label || emptyI18n()), [ln.code]: e.target.value } })}
                                   placeholder={`Label — ${ln.label}`}
                                   className="border border-line rounded px-2 py-1 text-xs" />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function stringifyError(e) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(" · ");
  if (typeof detail === "string") return detail;
  return String(e?.message || e);
}
